import cx from 'classnames';
import { useCallback, useRef, type FunctionComponent } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import ArrowRightIcon from '@atlaskit/icon/core/arrow-right';
import CalendarPlusIcon from '@atlaskit/icon/core/calendar-plus';
import CopyIcon from '@atlaskit/icon/core/copy';
import CrossIcon from '@atlaskit/icon/core/cross';
import TableCellMergeIcon from '@atlaskit/icon/core/table-cell-merge';
import { LabsLozenge } from '@trello/ai-labs';
import { FeedbackCollector } from '@trello/ai-labs/FeedbackCollector';
import { Analytics } from '@trello/atlassian-analytics';
import {
  smartScheduledCardsSharedState,
  type SmartScheduledCardPlannerEvent,
} from '@trello/business-logic/planner';
import { useFeatureGate } from '@trello/feature-gate-client';
import { client } from '@trello/graphql';
import { BoardIdProvider } from '@trello/id-context';
import { Key, Scope, useShortcut } from '@trello/keybindings';
import { Popover, PopoverPlacement, usePopover } from '@trello/nachos/popover';
import { PLANNER_CONSTANTS } from '@trello/planner';
import { useSharedStateSelector } from '@trello/shared-state';
import {
  useIsPlannerPanelOpen,
  useSplitScreenSharedState,
} from '@trello/split-screen';

import {
  IslandNav,
  IslandNavButton,
  IslandNavSeparator,
} from 'app/src/components/IslandNav';
import { CopyCardPopover } from 'app/src/components/MoveCardPopover/CopyCardPopover';
import { MoveCardPopoverBulk } from 'app/src/components/MoveCardPopover/MoveCardPopoverBulk';
import { BulkActionLoadingIsland } from './BulkActionLoadingIsland';
import { bulkActionSelectedCardsSharedState } from './bulkActionSelectedCardsSharedState';
import { useCardInfoFragment } from './CardInfoFragment.generated';
import { SmartScheduleCardFragmentDoc } from './SmartScheduleCardFragment.generated';
import { useSmartScheduleCardsMutation } from './SmartScheduleCardsMutation.generated';
import { SmartScheduleIslandNavigation } from './SmartScheduleIslandNavigation';
import { useIsBulkActionEnabled } from './useIsBulkActionEnabled';
import { useIsMergeCardsEnabled } from './useIsMergeCardsEnabled';
import { useMergeSelectedCards } from './useMergeSelectedCards';

import * as styles from './BulkActionIsland.module.less';

export interface BulkActionIslandProps {
  isVisible?: boolean;
}

export const BulkActionIsland: FunctionComponent<BulkActionIslandProps> = ({
  isVisible = false,
}) => {
  const intl = useIntl();
  const isBulkActionEnabled = useIsBulkActionEnabled();
  const { value: isSmartScheduleEnabled } = useFeatureGate(
    'phx_smart_schedule_m1',
  );
  const [smartScheduleCards] = useSmartScheduleCardsMutation();
  const smartScheduleTraceId = useRef<string | undefined>(undefined);

  const { isLoading: isSmartScheduleLoading, numProposedEvents } =
    useSharedStateSelector(
      smartScheduledCardsSharedState,
      useCallback(
        (state) => ({
          isLoading: state.isLoading,
          numProposedEvents: state.proposedEvents.length,
        }),
        [],
      ),
    );

  const numBoardsWithSelectedCards = useSharedStateSelector(
    bulkActionSelectedCardsSharedState,
    useCallback((state) => Object.keys(state.selectedCards).length, []),
  );

  // Using last selected card to get the target board and list for merge cards
  const lastSelectedCardId = useSharedStateSelector(
    bulkActionSelectedCardsSharedState,
    useCallback((state) => {
      const allCardIds: string[] = [];
      Object.values(state.selectedCards).forEach((boardCards) => {
        allCardIds.push(...Object.keys(boardCards));
      });
      return allCardIds.pop() ?? '';
    }, []),
  );

  const numSelectedCards = useSharedStateSelector(
    bulkActionSelectedCardsSharedState,
    useCallback((state) => {
      return Object.values(state.selectedCards).reduce(
        (count, boardCards) => count + Object.keys(boardCards).length,
        0,
      );
    }, []),
  );

  const cardInfo = useCardInfoFragment({
    from: {
      id: lastSelectedCardId,
    },
  }).data;

  const targetBoardId = cardInfo?.idBoard;
  const targetListId = cardInfo?.idList;

  const shouldShowMergeButton =
    useIsMergeCardsEnabled() && targetBoardId && targetListId;
  const shouldDisableMergeClick = numSelectedCards < 2;

  const {
    popoverProps: movePopoverProps,
    triggerRef: moveTriggerRef,
    toggle: toggleMove,
  } = usePopover({
    placement: PopoverPlacement.TOP,
  });

  const {
    popoverProps: copyPopoverProps,
    triggerRef: copyTriggerRef,
    toggle: toggleCopy,
  } = usePopover({
    placement: PopoverPlacement.TOP,
  });

  const isPlannerPanelOpen = useIsPlannerPanelOpen();
  const { togglePlanner } = useSplitScreenSharedState();

  const unSelectAllCards = useCallback(() => {
    bulkActionSelectedCardsSharedState.setValue(() => ({
      selectedCards: {},
      isLoading: false,
    }));
  }, []);

  useShortcut(unSelectAllCards, {
    key: Key.Escape,
    scope: Scope.Default,
  });

  const mergeSelectedCards = useMergeSelectedCards({
    source: 'bulkActionIsland',
    targetBoardId: targetBoardId ?? '',
    targetListId: targetListId ?? '',
  });

  const handleMerge = useCallback(() => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'mergeCardsButton',
      source: 'bulkActionIsland',
      attributes: {
        numSelectedCards,
        numBoardsWithSelectedCards,
      },
    });
    mergeSelectedCards();
  }, [mergeSelectedCards, numSelectedCards, numBoardsWithSelectedCards]);

  const handleSchedule = useCallback(() => {
    const offsetMinutes = new Date().getTimezoneOffset();
    const offsetHours = -offsetMinutes / 60;

    const traceId = Analytics.startTask({
      taskName: 'edit-plannerEvent/smartSchedule',
      source: 'bulkActionIsland',
    });

    smartScheduleTraceId.current = traceId;
    const allCardIds = Object.values(
      // get all objects for each board
      bulkActionSelectedCardsSharedState.value.selectedCards,
    ).reduce((acc, curr) => {
      // get all card IDs that are `true` for the board
      Object.keys(curr).forEach((cardId) => {
        if (curr[cardId]) {
          acc.push(cardId);
        }
      });
      return acc;
    }, [] as string[]);

    smartScheduledCardsSharedState.setValue({
      ...smartScheduledCardsSharedState.value,
      isLoading: true,
    });

    smartScheduleCards({
      variables: {
        cardIds: allCardIds,
        timezoneOffsetHours: offsetHours,
        traceId,
      },
    })
      .then(({ data }) => {
        const proposedEvents = data?.smartScheduleCards?.scheduled_cards
          // if the LLM determines a card shouldn't be scheduled, it will not have a start time
          ?.filter((card) => card.start_time)
          .map((card) => {
            const cardData = client.readFragment({
              id: `Card:${card.card_id}`,
              fragment: SmartScheduleCardFragmentDoc,
            });
            return {
              id: card.card_id,
              start: card.start_time,
              end: card.end_time,
              eventId:
                PLANNER_CONSTANTS.PROPOSED_EVENT_ID_PREFIX + card.card_id,
              title: cardData?.name,
              durationEditable: true,
              startEditable: true,
            } as SmartScheduledCardPlannerEvent;
          });
        smartScheduledCardsSharedState.setValue({
          activeIndex: 0,
          isLoading: false,
          proposedEvents: [
            ...smartScheduledCardsSharedState.value.proposedEvents,
            ...(proposedEvents ?? []),
          ],
        });
        Analytics.taskSucceeded({
          taskName: 'edit-plannerEvent/smartSchedule',
          traceId,
          source: 'bulkActionIsland',
        });
        if (proposedEvents?.length && !isPlannerPanelOpen) {
          togglePlanner();
        }
        unSelectAllCards();
      })
      .catch((error) => {
        Analytics.taskFailed({
          taskName: 'edit-plannerEvent/smartSchedule',
          traceId,
          source: 'bulkActionIsland',
          error,
        });

        smartScheduledCardsSharedState.setValue({
          ...smartScheduledCardsSharedState.value,
          isLoading: false,
        });
      });
  }, [smartScheduleCards, unSelectAllCards, isPlannerPanelOpen, togglePlanner]);

  if (!isBulkActionEnabled) {
    return null;
  }

  if (isSmartScheduleLoading) {
    return <BulkActionLoadingIsland />;
  }

  if (numProposedEvents > 0) {
    return (
      <>
        <IslandNav
          buttonGroupClassName={styles.buttonGroup}
          className={cx(styles.bulkActionIsland, {
            [styles.visible]: isVisible,
            [styles.hidden]: !isVisible,
          })}
        >
          <SmartScheduleIslandNavigation />
        </IslandNav>

        <FeedbackCollector
          featureName={'smartScheduling'}
          flagTreatment
          traceId={smartScheduleTraceId.current}
        />
      </>
    );
  }

  const firstBoardId =
    Object.keys(bulkActionSelectedCardsSharedState.value.selectedCards)?.[0] ??
    '';

  return (
    <>
      <IslandNav
        buttonGroupClassName={styles.buttonGroup}
        className={cx(styles.bulkActionIsland, {
          [styles.visible]: isVisible,
          [styles.hidden]: !isVisible,
        })}
      >
        {isSmartScheduleEnabled && (
          <IslandNavButton
            icon={
              <CalendarPlusIcon
                label={intl.formatMessage({
                  id: 'templates.bulk-actions.schedule',
                  defaultMessage: 'Schedule',
                  description: 'Schedule icon label',
                })}
              />
            }
            onClick={handleSchedule}
            role="checkbox"
            title={intl.formatMessage({
              id: 'templates.bulk-actions.schedule',
              defaultMessage: 'Schedule',
              description: 'Schedule button label',
            })}
            label={
              <FormattedMessage
                id="templates.bulk-actions.schedule"
                defaultMessage="Schedule"
                description="Schedule button label"
              />
            }
            aria-label={intl.formatMessage({
              id: 'templates.bulk-actions.schedule',
              defaultMessage: 'Schedule',
              description: 'Schedule button label',
            })}
            showLabels
          />
        )}
        {shouldShowMergeButton && (
          <IslandNavButton
            icon={
              <TableCellMergeIcon
                label={intl.formatMessage({
                  id: 'templates.bulk-actions.merge',
                  defaultMessage: 'Merge',
                  description: 'Merge icon label',
                })}
              />
            }
            onClick={handleMerge}
            role="checkbox"
            title={intl.formatMessage({
              id: 'templates.bulk-actions.merge',
              defaultMessage: 'Merge',
              description: 'Merge button label',
            })}
            label={
              <span className={styles.mergeLabel}>
                <FormattedMessage
                  id="templates.bulk-actions.merge"
                  defaultMessage="Merge"
                  description="Merge button label"
                />{' '}
                <LabsLozenge isDisabled={shouldDisableMergeClick} />
              </span>
            }
            aria-label={intl.formatMessage({
              id: 'templates.bulk-actions.merge',
              defaultMessage: 'Merge',
              description: 'Merge button label',
            })}
            showLabels
            isDisabled={shouldDisableMergeClick}
          />
        )}
        {numBoardsWithSelectedCards === 1 && (
          <>
            <IslandNavButton
              ref={moveTriggerRef}
              icon={
                <ArrowRightIcon
                  label={intl.formatMessage({
                    id: 'templates.bulk-actions.move',
                    defaultMessage: 'Move',
                    description: 'Move icon label',
                  })}
                />
              }
              onClick={toggleMove}
              isSelected={movePopoverProps.isVisible}
              role="checkbox"
              title={intl.formatMessage({
                id: 'templates.bulk-actions.move',
                defaultMessage: 'Move',
                description: 'Move button label',
              })}
              label={
                <FormattedMessage
                  id="templates.bulk-actions.move"
                  defaultMessage="Move"
                  description="Move button label"
                />
              }
              aria-label={intl.formatMessage({
                id: 'templates.bulk-actions.move',
                defaultMessage: 'Move',
                description: 'Move button label',
              })}
              showLabels
            />
            <IslandNavButton
              ref={copyTriggerRef}
              icon={
                <CopyIcon
                  label={intl.formatMessage({
                    id: 'templates.bulk-actions.copy',
                    defaultMessage: 'Copy',
                    description: 'Copy icon label',
                  })}
                />
              }
              onClick={toggleCopy}
              isSelected={copyPopoverProps.isVisible}
              role="checkbox"
              title={intl.formatMessage({
                id: 'templates.bulk-actions.copy',
                defaultMessage: 'Copy',
                description: 'Copy button label',
              })}
              label={
                <FormattedMessage
                  id="templates.bulk-actions.copy"
                  defaultMessage="Copy"
                  description="Copy button label"
                />
              }
              aria-label={intl.formatMessage({
                id: 'templates.bulk-actions.copy',
                defaultMessage: 'Copy',
                description: 'Copy button label',
              })}
              showLabels
            />
          </>
        )}
        <IslandNavSeparator />
        <IslandNavButton
          icon={
            <CrossIcon
              label={intl.formatMessage({
                id: 'templates.bulk-actions.unselect',
                defaultMessage: 'Unselect',
                description: 'Unselect icon label',
              })}
            />
          }
          onClick={unSelectAllCards}
          title={intl.formatMessage({
            id: 'templates.bulk-actions.unselect',
            defaultMessage: 'Unselect',
            description: 'Unselect button label',
          })}
          label={
            <FormattedMessage
              id="templates.bulk-actions.unselect"
              defaultMessage="Unselect"
              description="Unselect button label"
            />
          }
          aria-label={intl.formatMessage({
            id: 'templates.bulk-actions.unselect',
            defaultMessage: 'Unselect',
            description: 'Unselect button label',
          })}
          showLabels={false}
        />
      </IslandNav>
      <BoardIdProvider value={firstBoardId}>
        <Popover {...movePopoverProps}>
          <MoveCardPopoverBulk onClose={toggleMove} boardId={firstBoardId} />
        </Popover>
        <Popover {...copyPopoverProps}>
          <CopyCardPopover isBulk onClose={toggleCopy} />
        </Popover>
      </BoardIdProvider>
    </>
  );
};
